import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiMapPin,
} from "react-icons/fi";

import { getEvents } from "../services/eventsServices";
import { toAbsolutePublicUrl } from "../services/mediaLibraryServices";
import Reveal from "./ui/Reveal";

import EventDetailModal from "../pages/Events/components/EventDetailModal";

const FALLBACK_EVENT_IMAGE = "/images/agenda-fallback.jpg";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const stripHtml = (html = "") =>
  String(html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

const makeExcerpt = (html = "", max = 170) => {
  const text = stripHtml(html);
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const parseDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const formatDateParts = (value) => {
  const date = parseDate(value);
  if (!date) return null;

  return {
    day: new Intl.DateTimeFormat("fr-FR", { day: "2-digit" }).format(date),
    month: new Intl.DateTimeFormat("fr-FR", { month: "short" })
      .format(date)
      .replace(".", "")
      .toUpperCase(),
    year: new Intl.DateTimeFormat("fr-FR", { year: "numeric" }).format(date),
    time: new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date),
  };
};

const formatPeriodLabel = (startValue, endValue, allDay = false) => {
  const start = parseDate(startValue);
  const end = parseDate(endValue);

  if (!start) return "";

  const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (!end || isSameDay(start, end)) {
    return allDay
      ? dateFormatter.format(start)
      : `${dateFormatter.format(start)} à ${timeFormatter.format(start)}`;
  }

  return `${dateFormatter.format(start)} au ${dateFormatter.format(end)}`;
};

const getEventStatus = (startValue, endValue) => {
  const now = new Date();
  const start = parseDate(startValue);
  const end = parseDate(endValue);

  if (!start) return "upcoming";

  if (end && now >= start && now <= end) return "ongoing";
  if (!end && isSameDay(now, start)) return "ongoing";
  if ((end && end < now) || (!end && start < now && !isSameDay(now, start))) {
    return "past";
  }

  return "upcoming";
};

const getEventStatusLabel = (status) => {
  if (status === "past") return "Terminé";
  if (status === "ongoing") return "En cours";
  return "À venir";
};

const getVisibleCount = () => {
  if (typeof window === "undefined") return 3;
  if (window.innerWidth >= 1180) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
};

function useVisibleCount() {
  const [visibleCount, setVisibleCount] = useState(getVisibleCount);

  useEffect(() => {
    const onResize = () => {
      setVisibleCount(getVisibleCount());
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return visibleCount;
}

const normalizeEvent = (event = {}) => {
  const startDate = event.start_date || event.startDate || event.date || null;
  const endDate = event.end_date || event.endDate || null;

  const rawImage =
    event.photo_vignette ||
    event.image_url ||
    event.imageUrl ||
    event.image ||
    "";

  const image = rawImage ? toAbsolutePublicUrl(rawImage) : FALLBACK_EVENT_IMAGE;

  const descriptionRaw =
    event.description || event.descriptionRaw || event.content || "";

  const allDay = Boolean(event.all_day || event.allDay || event.is_all_day);
  const status = getEventStatus(startDate, endDate);

  return {
    ...event,
    id: event.id,
    title: event.title || event.titre || "Sans titre",
    subtitle: event.subtitle || event.sous_titre || "",
    category: event.categorie || event.category || "",
    location: event.location || event.lieu || event.adresse || "",
    image,
    startDate,
    endDate,
    allDay,
    status,
    dateParts: formatDateParts(startDate),
    endDateParts: formatDateParts(endDate),
    periodLabel: formatPeriodLabel(startDate, endDate, allDay),
    dateLabel: formatPeriodLabel(startDate, endDate, allDay),
    externalLink: event.external_link || event.externalLink || "",
    descriptionRaw,
    descriptionText: stripHtml(descriptionRaw),
    descriptionPreview: makeExcerpt(descriptionRaw),
  };
};

function EventDateBadge({ event }) {
  const startDate = parseDate(event.startDate);
  const endDate = parseDate(event.endDate);

  const dateParts = event.dateParts;
  const endDateParts = event.endDateParts;

  const hasEndDate = startDate && endDate && !isSameDay(startDate, endDate);
  const differentYear =
    startDate && endDate && startDate.getFullYear() !== endDate.getFullYear();

  if (!dateParts) return null;

  return (
    <div className="absolute left-4 top-4 z-10 min-w-[74px] overflow-hidden rounded-2xl bg-white/95 text-center shadow-lg backdrop-blur dark:bg-slate-950/95">
      <div className="bg-[#1277B9] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
        {dateParts.month}
      </div>

      <div className="px-3 py-2">
        <p className="text-2xl font-bold leading-none text-slate-950 dark:text-white">
          {dateParts.day}
        </p>

        <p className="mt-1 text-[11px] font-bold text-slate-400">
          {event.allDay ? dateParts.year : dateParts.time || dateParts.year}
        </p>
      </div>

      {hasEndDate && endDateParts && (
        <div className="border-t border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-900">
          <p className="text-[7px] font-semibold uppercase tracking-[0.16em] text-slate-900 dark:text-slate-300">
            jusqu’au
          </p>

          <p className="mt-0.5 text-[11px] font-bold uppercase leading-none text-[#1277B9] dark:text-sky-300">
            {endDateParts.day} {endDateParts.month}
          </p>

          {differentYear && (
            <p className="mt-0.5 text-[10px] font-bold text-slate-400">
              {endDateParts.year}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function EventStatusPill({ status }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold",
        status === "past"
          ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
          : status === "ongoing"
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            : "bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300",
      )}
    >
      {getEventStatusLabel(status)}
    </span>
  );
}

function EventCard({ event, onOpen, delay = 0 }) {
  const handleOpen = () => {
    onOpen?.(event);
  };

  return (
    <Reveal delay={delay} className="h-full">
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-950">
        <button
          type="button"
          onClick={handleOpen}
          className="relative block overflow-hidden bg-slate-100 text-left dark:bg-slate-900"
          aria-label={`Voir l’événement ${event.title}`}
        >
          <img
            src={event.image}
            alt={event.title}
            loading="lazy"
            className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-105"
            onError={(eventImage) => {
              eventImage.currentTarget.src = FALLBACK_EVENT_IMAGE;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-950/10 to-transparent" />

          <EventDateBadge event={event} />

          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
            {event.category && (
              <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#1277B9] shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-sky-300">
                {event.category}
              </span>
            )}

            <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-slate-700 shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-slate-200">
              {event.allDay ? "Journée complète" : event.dateParts?.time}
            </span>
          </div>
        </button>

        <div className="flex flex-1 flex-col p-5">
          <div className="flex flex-wrap items-center gap-2">
            <EventStatusPill status={event.status} />

            {!event.allDay && event.dateParts?.time && (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                {event.dateParts.time}
              </span>
            )}

            {event.externalLink && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
                <FiExternalLink className="h-3.5 w-3.5" />
                Lien
              </span>
            )}
          </div>

          <h3 className="mt-3 line-clamp-2 text-xl font-bold leading-snug text-slate-950 dark:text-white">
            {event.title || "Sans titre"}
          </h3>

          {event.subtitle && (
            <p className="mt-1 line-clamp-1 text-sm font-bold text-[#1277B9] dark:text-sky-300">
              {event.subtitle}
            </p>
          )}

          {event.periodLabel && (
            <div className="mt-4 flex items-start gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
              <FiCalendar className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{event.periodLabel}</span>
            </div>
          )}

          {event.location && (
            <div className="mt-2 flex items-start gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
              <FiMapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="line-clamp-2">{event.location}</span>
            </div>
          )}

          <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {event.descriptionPreview || "Aucune description renseignée."}
          </p>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <button
              type="button"
              onClick={handleOpen}
              className="group/btn inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40 sm:flex-none"
            >
              Voir l’événement
              <FiArrowRight className="h-4 w-4 transition group-hover/btn:translate-x-1" />
            </button>

            {event.externalLink && (
              <a
                href={event.externalLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 sm:flex-none"
              >
                Site externe
                <FiExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function EventSkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="aspect-[16/10] animate-pulse bg-slate-200 dark:bg-slate-800" />
      <div className="space-y-4 p-5">
        <div className="h-6 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-5/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>
        <div className="h-11 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function AgendaSection() {
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const visibleCount = useVisibleCount();
  const touchStartX = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchEvents = async () => {
      try {
        const data = (await getEvents()) || [];

        if (!mounted) return;

        const now = new Date();
        const startOfToday = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        ).getTime();

        const normalized = (Array.isArray(data) ? data : [])
          .filter((event) => event?.is_active !== false)
          .map(normalizeEvent)
          .filter((event) => {
            const start = parseDate(event.startDate);
            const end = parseDate(event.endDate);

            if (!start) return true;
            if (end) return end.getTime() >= startOfToday;

            return start.getTime() >= startOfToday;
          })
          .sort((a, b) => {
            const dateA = parseDate(a.startDate)?.getTime() || 0;
            const dateB = parseDate(b.startDate)?.getTime() || 0;
            return dateA - dateB;
          });

        setRawItems(normalized);
      } catch (error) {
        console.error("Erreur chargement événements:", error);

        if (mounted) {
          setRawItems([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => rawItems, [rawItems]);

  useEffect(() => {
    setStartIndex(0);
  }, [visibleCount]);

  const canNavigate = items.length > visibleCount;

  const visibleItems = useMemo(() => {
    if (!items.length) return [];

    if (items.length <= visibleCount) return items;

    return Array.from({ length: visibleCount }).map((_, offset) => {
      const index = (startIndex + offset) % items.length;
      return items[index];
    });
  }, [items, startIndex, visibleCount]);

  const prev = () => {
    if (!items.length) return;

    setStartIndex((current) => (current - 1 + items.length) % items.length);
  };

  const next = () => {
    if (!items.length) return;

    setStartIndex((current) => (current + 1) % items.length);
  };

  useEffect(() => {
    if (!canNavigate || paused || selectedEvent) return;

    const timer = window.setInterval(() => {
      setStartIndex((current) => (current + 1) % items.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, [canNavigate, paused, selectedEvent, items.length]);

  const onTouchStart = (event) => {
    touchStartX.current = event.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (event) => {
    const start = touchStartX.current;
    const end = event.changedTouches?.[0]?.clientX ?? null;

    if (start == null || end == null) return;

    const delta = end - start;

    if (delta > 45) prev();
    if (delta < -45) next();

    touchStartX.current = null;
  };

  const openEventDetails = (event) => {
    if (!event) return;

    setSelectedEvent(event);
    setPaused(true);
  };

  const closeEventDetails = () => {
    setSelectedEvent(null);
    setPaused(false);
  };

  if (!loading && !items.length) return null;

  return (
    <section
      id="agenda"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="agenda-home-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                Agenda municipal
              </div>

              <h2
                id="agenda-home-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Les prochains événements
              </h2>

              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Retrouvez les animations, rendez-vous, réunions et temps forts à
                venir dans la commune.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <Link
              to="/agenda"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/40 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:border-sky-500/40 dark:hover:text-sky-300"
            >
              Tous les événements
              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {loading
              ? Array.from({ length: visibleCount }).map((_, index) => (
                  <EventSkeletonCard key={index} />
                ))
              : visibleItems.map((event, index) => (
                  <EventCard
                    key={`${event.id || event.title}-${startIndex}-${index}`}
                    event={event}
                    onOpen={openEventDetails}
                    delay={index * 100}
                  />
                ))}
          </div>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-0 top-1/2 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-xl shadow-slate-950/10 transition hover:-translate-x-[55%] hover:bg-slate-50 hover:text-[#1277B9] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/30 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 lg:inline-flex"
                aria-label="Événement précédent"
              >
                <FiChevronLeft className="h-6 w-6" />
              </button>

              <button
                type="button"
                onClick={next}
                className="absolute right-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-xl shadow-slate-950/10 transition hover:translate-x-[55%] hover:bg-slate-50 hover:text-[#1277B9] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/30 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 lg:inline-flex"
                aria-label="Événement suivant"
              >
                <FiChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={!canNavigate}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-[#1277B9] disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
              aria-label="Événement précédent"
            >
              <FiArrowLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={next}
              disabled={!canNavigate}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1277B9] text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Événement suivant"
            >
              <FiArrowRight className="h-5 w-5" />
            </button>

            <span className="ml-2 text-sm font-bold text-slate-500 dark:text-slate-400">
              {items.length
                ? `${startIndex + 1}/${items.length}`
                : loading
                  ? "Chargement…"
                  : "—"}
            </span>
          </div>

          {items.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              {items.map((event, dotIndex) => (
                <button
                  key={event.id || `${event.title}-${dotIndex}`}
                  type="button"
                  onClick={() => setStartIndex(dotIndex)}
                  aria-label={`Afficher l’événement ${dotIndex + 1}`}
                  className={`h-2.5 rounded-full transition-all ${
                    dotIndex === startIndex
                      ? "w-9 bg-[#1277B9]"
                      : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <EventDetailModal event={selectedEvent} onClose={closeEventDetails} />
    </section>
  );
}